<template>
    <form-fields>
        <toolbar-buttons>
            <text-entry-button
                :placeholder="$t('V3_EDIT_TEXT_PLACEHOLDER')"
                v-model.trim="selectedText"
                @input="updateText"
                :hint="approved !== false ? $t('V3_EDIT_TEXT_HINT') : $t('V3_EDIT_TEXT_VALIDATION')"
                :hint-error="approved === false"
            />
        </toolbar-buttons>

        <step-buttons
            :class="{
                'cz3__hide-lg': buttonsSm
            }"
        >
            <secondary-button
                class="cz3__flex-1"
                :label="$t('V3_BACK')"
                @click="back"
            />

            <action-button
                class="cz3__flex-1"
                :label="$t('V3_NEXT')"
                :disabled="!textEntered || approved === false"
                @click="next"
            />
        </step-buttons>
    </form-fields>
</template>

<style lang="scss">
</style>

<script>
    import ToolbarButtons from './ToolbarButtons.vue';
    import FormFields from './FormFields.vue';
    import ActionButton from './ActionButton.vue';
    import SecondaryButton from './SecondaryButton.vue';
    import StepButtons from './StepButtons.vue';
    import TextEntryButton from './TextEntryButton.vue';

    import * as constants from '../../constants';

    // eslint-disable-next-line no-unused-vars
    import designArea from '../../utilities/designArea';
    // eslint-disable-next-line no-unused-vars
    import extent from '../../utilities/extent';

    let lastUnique = 0;

    function unique(prefix) {
        lastUnique += 1;

        return `${prefix}${lastUnique}`;
    }

    export default {
        components: {
            ToolbarButtons,
            FormFields,
            ActionButton,
            SecondaryButton,
            StepButtons,
            TextEntryButton,
        },

        props: {
            buttonsSm: Boolean,
        },

        data() {
            return {
                selectedText: '',

                approved: null,
            };
        },

        computed: {
            viewController() {
                return this.$store.state.customizer.viewController;
            },

            currentView() {
                return this.$store.state.customizer.currentView;
            },

            editPlacements() {
                return this.$store.state.customizer.editPlacements;
            },

            editPlacement() {
                if (this.editPlacements && this.editPlacements.length > 0) {
                    return this.editPlacements[0];
                }

                return null;
            },

            editComponent() {
                if (this.editPlacement) {
                    return this.viewController.selectedAtPlacement(this.editPlacement.code);
                }

                return null;
            },

            filledTextPlacements() {
                return this.$store.getters.filledTextPlacements;
            },

            filledPlacements() {
                return this.$store.getters.filledPlacements;
            },

            emptyTextPlacements() {
                return this.$store.getters.emptyTextPlacements;
            },

            personalizationMethod() {
                return this.$store.state.customizer.personalizationMethod;
            },

            nextStep() {
                return this.$store.getters.nextStepV3;
            },

            textEntered() {
                return this.selectedText?.length > 0;
            },

            /**
             * Secondary bottles array.
             */
            secondaryBottles() {
                return this.$store.state.customizer.secondaryBottles;
            },
        },

        mounted() {
            if (this.editPlacement == null) {
                this.addText();
            } else {
                this.loadFromPlacement();
            }
        },

        methods: {
            back() {
                this.$store.dispatch('setSelectedSubStepV3', 'choose');
            },

            /**
             * Update personalization.
             */
            updateText() {
                const moderationRequest = unique('moderate-');

                this.lastModerationRequest = moderationRequest;

                const text = this.selectedText;

                if (text == null || text.trim().length === 0) {
                    this.applyText('');
                    return;
                }

                this.viewController.reviewText(text).then((result) => {
                    if (moderationRequest === this.lastModerationRequest) {
                        if (result.hasProfanity) {
                            this.approved = false;

                            this.$emit('approved', false);
                        } else {
                            this.applyText(text);
                        }
                    }
                });
            },

            applyText(text) {
                this.approved = true;

                this.$emit('approved', true);

                this.viewController.updatePersonalization(this.editPlacement.code, text).then((applyResult) => {
                    if (applyResult.accepted) {
                        if (applyResult.as) {
                            this.selectedText = applyResult.as;
                        }
                    } else if (applyResult.lastValid != null) {
                        this.selectedText = applyResult.lastValid;
                    }
                });
            },

            next() {
                if (this.personalizationMethod === 'printing') {
                    this.$store.dispatch('setSelectedSubStepV3', 'text-color');
                } else {
                    this.$store.dispatch('setSelectedSubStepV3', 'text-font');
                }
            },

            addText() {
                if (this.emptyTextPlacements.length > 0) {
                    this.$store.dispatch('validateEditableViews');
                    this.$store.dispatch('switchToCommonView');

                    const { placement, component } = this.emptyTextPlacements[0];

                    this.$store.dispatch('takeEmptyPlacement', placement);

                    // ... and center manually in the current view.
                    if (this.currentView) {
                        const { view } = this.currentView;

                        this.$store.dispatch('renderOnlyOnView', {
                            view: this.currentView,
                        });

                        if (view.custom) {
                            const {
                                left, right, top, bottom,
                            } = designArea(this.$store.state.customizer, view.custom['v-design-area']);

                            const width = +component.custom['personalization-width'];
                            const height = +component.custom['personalization-height'];

                            if (left != null && right != null && top != null && bottom != null) {
                                let yOffset = null;
                                let yOffsetMin = null;

                                let fontScale = null;
                                let fontColor = null;
                                let fontFamily = null;

                                this.filledPlacements.forEach((p) => {
                                    if (this.personalizationMethod === 'printing') {
                                        return;
                                    }

                                    if (p.component.custom['personalization-y'] != null) {
                                        if (p.placement.code.startsWith(constants.PLACEMENT_MONOGRAM)) {
                                            const below = +p.component.custom['personalization-y'] + (+p.component.custom['personalization-height']) * 1.5;

                                            if (yOffset == null) {
                                                yOffset = below;
                                            } else {
                                                yOffset = Math.max(below, yOffset);
                                            }

                                            const above = +p.component.custom['personalization-y'] - (+p.component.custom['personalization-height']) * 0.5;

                                            if (yOffsetMin == null) {
                                                yOffsetMin = above;
                                            } else {
                                                yOffsetMin = Math.min(above, yOffsetMin);
                                            }
                                        } else {
                                            const below = +p.component.custom['personalization-y'] + (+p.component.custom['personalization-height']) * 1.5;

                                            if (yOffset == null) {
                                                yOffset = below;
                                            } else {
                                                yOffset = Math.max(below, yOffset);
                                            }

                                            const above = +p.component.custom['personalization-y'] - (+p.component.custom['personalization-height']) * 0.5;

                                            if (yOffsetMin == null) {
                                                yOffsetMin = above;
                                            } else {
                                                yOffsetMin = Math.min(above, yOffsetMin);
                                            }
                                        }
                                    }

                                    if (p.component.custom['dynamic-image-height'] != null) {
                                        const y = +p.component.custom['dynamic-image-offset-y'];
                                        const h = +p.component.custom['dynamic-image-height'];
                                        const s = +p.component.custom['dynamic-image-scale'];
                                        const totalHeight = +p.component.custom['dynamic-image-area-height'];

                                        const below = y + (h * s * 0.5) + (totalHeight * 0.1);

                                        if (yOffset == null) {
                                            yOffset = below;
                                        } else {
                                            yOffset = Math.max(below, yOffset);
                                        }

                                        const above = y - (h * s * 0.5) - (totalHeight * 0.1);

                                        if (yOffsetMin == null) {
                                            yOffsetMin = above;
                                        } else {
                                            yOffsetMin = Math.min(above, yOffsetMin);
                                        }
                                    }

                                    if (p.component.custom['personalization-color'] != null) {
                                        fontColor = p.component.custom['personalization-color'];
                                    }

                                    if (p.component.custom['personalization-scale'] != null) {
                                        fontScale = p.component.custom['personalization-scale'];
                                    }

                                    const font = p.component.description.find((d) => d.definition === constants.DEFINITION_FONT);
                                    if (font) {
                                        fontFamily = font.code;
                                    }
                                });

                                if (yOffset == null) {
                                    if ((this.viewController.blueprint.custom['v-default-alignment-v3'] || '').toLowerCase() === 'top') {
                                        yOffset = top + (+(component.custom['personalization-size'])) + 10;
                                    } else {
                                        yOffset = (((top + bottom) * 0.5) - (height * 0.5));
                                    }
                                } else {
                                    // Check if we a going to be below the fold.
                                    // eslint-disable-next-line no-lonely-if
                                    if (yOffset + ((bottom - top) * 0.1) >= bottom) {
                                        if (yOffsetMin > top) {
                                            yOffset = yOffsetMin;
                                        } else {
                                            yOffset = (((top + bottom) * 0.5) - (height * 0.5));
                                        }
                                    }
                                }

                                const newSettings = {
                                    'v-design-on': view.custom['v-design-area'],
                                    mask: view.custom['v-design-mask'],
                                    'personalization-x': ((left + right) * 0.5) - (width * 0.5),
                                    'personalization-y': yOffset,
                                    'personalization-rotate': 0,
                                    'personalization-scale': 1,
                                    'personalization-filled': 'true',
                                    'personalization-width': '1',
                                    'personalization-height': '1',
                                    'personalization-size-fit-min': 'none',
                                };

                                if (fontScale) {
                                    newSettings['personalization-scale'] = fontScale;
                                }

                                if (fontColor) {
                                    newSettings['personalization-color'] = fontColor;
                                }

                                const group = this.viewController.blueprint.custom[`v-group-${view.custom['v-design-area']}`];

                                this.$store.dispatch('setGroup', { placement: placement.code, group });

                                if (fontFamily) {
                                    this.viewController.updateSimilarComponent(placement.code, constants.DEFINITION_FONT, fontFamily);
                                }

                                this.viewController.commitCustomAttributes(placement.code, newSettings, {
                                    propagate: true,
                                });
                            }
                        }
                    }

                    // Add lines to secondary bottles
                    if (this.secondaryBottles.length > 0) {
                        const newSecondaryBottles = this.secondaryBottles.map((secondaryBottle) => {
                            const newBottle = secondaryBottle;
                            secondaryBottle.push('');
                            return newBottle;
                        });

                        this.$store.dispatch('setSecondaryBottles', newSecondaryBottles);
                    }

                    this.beginEdit(placement);
                }
            },

            /**
             * Enters text editing mode.
             */
            beginEdit(placement) {
                this.$store.dispatch('lockSwipe', true);
                this.$store.dispatch('editPlacement', placement);

                this.loadFromPlacement();
            },

            loadFromPlacement() {
                this.selectedText = this.editComponent.custom['personalization-text'] || '';

                // const font = component.description.find((d) => d.definition === constants.DEFINITION_FONT);
                // if (font) {
                //     this.selectedFont = { ...font, ...{ custom: { ...font.custom, ...component.custom } } };
                // }

                if (this.editComponent.custom['v-design-on']) {
                    this.$store.dispatch('switchToDesignView', this.editComponent.custom['v-design-on']);
                }
            },
        },
    };
</script>
